import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { CommentComponent } from '../../../../shared/comment/comment.component';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { Achievement, labelsByAchievement } from '../../../../shared/models/enums/achievement.enum';
import { ContractStatus, labelsByContractStatus } from '../../../../shared/models/enums/contract-status.enum';
import {
  ContractType,
  WorkflowedContractType,
  labelsByAllContractType,
  labelsByWorkflowedTypedContractType,
} from '../../../../shared/models/enums/contract-type.enum';
import { TPermissionContext } from '../../../../shared/permissions/permissions';
import { ContractsService } from '../../../../shared/services/contracts.service';
import { DateService } from '../../../../shared/services/date.service';
import { StringUtils } from '../../../../shared/services/stringUtils.service';
import { SignatureComponent } from '../../../../shared/signature/signature.component';
import { ToastService } from '../../../../shared/toast/toast.service';
import { ContractTraceabilityStatus } from '../../../../shared/traceability/contract-traceability-status.enum';
import { TraceabilityDto } from '../../../../shared/traceability/traceability.dto';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { ContractDto } from '../contract.dto';
import { DatesNewContractDto } from './dates-new-contract-dto';

@Component({
  selector: 'app-contract-details',
  templateUrl: './contract-details.component.html',
  styleUrls: ['./contract-details.component.scss'],
  standalone: false,
})
export class ContractDetailsComponent implements OnInit {
  stringAscOrder = StringUtils.stringAscOrder;

  public contractDetailsForm: UntypedFormGroup;
  public submitted: boolean = false;
  public isWorkflowedContract: boolean = false;
  public workflowedContractPermissionContext: TPermissionContext;
  public isCreation: boolean = false;
  public isContractEnded: boolean = false;
  public traceabilityStatus = ContractTraceabilityStatus;
  public user: UserDto;

  private readonly destroy$: Subject<void> = new Subject();
  public contractTypes: string[] = labelsByAllContractType;
  public readonly statusMap: string[] = labelsByContractStatus;
  public readonly achievements: Map<Achievement, string> = labelsByAchievement;
  public readonly StringUtils: StringUtils = StringUtils;
  private readonly currentDate: Date;
  protected contractTypeIsSelected: boolean;
  public datesNewContract: DatesNewContractDto;

  @Input() public beneficiary: BeneficiaryDto;
  @Input() public contract: ContractDto;
  @Input() public contractTypeSelected: ContractType;

  @Output() closeModal = new EventEmitter<void>();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly contractsService: ContractsService,
    private readonly toastService: ToastService,
    private readonly dateService: DateService,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
  ) {
    this.currentDate = this.dateService.getCurrent();
  }

  async ngOnInit(): Promise<void> {
    this.contractTypeIsSelected = this.contract?.id ? true : false;
    this.user = await this.authService.getUser();
    this.isWorkflowedContract = labelsByWorkflowedTypedContractType.includes(this.contract?.contractType);

    this.workflowedContractPermissionContext =
      this.contractTypeSelected === ContractType.StartingIERContract
        ? 'CONTRACT_IER'
        : this.contractTypeSelected === ContractType.SocioPro
          ? 'CONTRACT_SOCIO_PRO'
          : undefined;
    this.isCreation = !this.contract;

    this.datesNewContract = await this.contractsService.getDatesContract(this.beneficiary.id);
    await this.loadForm();

    this.contractDetailsForm.controls.status.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: ContractStatus) => this.onStatusChanged(value));
    this.contractDetailsForm.controls.contractType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: ContractType) => this.onTypeChanged(value, false));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['contractTypeSelected'] && this.contractDetailsForm) {
      this.contractDetailsForm.controls.contractType.setValue(changes['contractTypeSelected'].currentValue);
    }
  }

  /**
   * Charger formulaire de consultation contrat
   * @param contractId
   */
  private async loadForm(): Promise<void> {
    this.contractDetailsForm = this.formBuilder.group(
      {
        beginDate: [
          { value: this.contract?.beginDate, disabled: !this.contractTypeIsSelected },
          [Validators.required, DateValidators.dateFormatValidator],
        ],
        endDate: [
          { value: this.contract?.endDate, disabled: !this.contractTypeIsSelected },
          [Validators.required, DateValidators.dateFormatValidator],
        ],
        contractType: [{ value: this.contractTypeSelected, disabled: this.isWorkflowedContract }, [Validators.required]],
        mainPurpose: [{ value: this.contract?.mainPurpose, disabled: !this.contractTypeIsSelected }, Validators.required],
        status: [{ value: this.contract?.status, disabled: !this.contractTypeIsSelected }, Validators.required],
        achievement: [{ value: this.contract?.achievement, disabled: !this.contractTypeIsSelected }],
        project: [{ value: this.contract?.project, disabled: !this.contractTypeIsSelected }],
        actionsToPerform: [{ value: this.contract?.actionsToPerform, disabled: !this.contractTypeIsSelected }],
        statusDate: [{ value: this.contract?.statusDate, disabled: true }],
      },
      {
        validators: [DateValidators.datesOrderValidator('beginDate', 'endDate')],
      },
    );
    if (this.contractTypeSelected) {
      this.onTypeChanged(this.contractTypeSelected as ContractType, !!this.contract?.id); // true: Prevent replacing existing date
    }
    if (this.contract) {
      this.onStatusChanged(this.contract.status as ContractStatus, true);
    }
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (!this.contractDetailsForm.valid) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
      return;
    }

    const toSaveContract = new ContractDto(
      this.contract ? this.contract.id : undefined,
      this.contractDetailsForm.controls.beginDate.value,
      this.contractDetailsForm.controls.endDate.value,
      this.beneficiary.id,
      this.contractDetailsForm.controls.contractType.value,
      this.contractDetailsForm.controls.status.value,
      {
        mainPurpose: this.contractDetailsForm.controls.mainPurpose.value,
        achievement: this.contractDetailsForm.controls.achievement.value,
        project: this.contractDetailsForm.controls.project.value,
        actionsToPerform: this.contractDetailsForm.controls.actionsToPerform.value,
        statusDate: this.contractDetailsForm.controls.statusDate.value,
      },
    );
    if (this.contract?.id) {
      await this.updateContract(toSaveContract);
    } else {
      await this.createContract(toSaveContract);
    }
  }

  private async createContract(toSaveContract: ContractDto) {
    try {
      await this.contractsService.saveContract(toSaveContract);
      this.toastService.show('Le contrat a été ajouté avec succès.', 'success');
      this.closeModal.emit();
    } catch (error) {
      if (error.status === 470) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas d'accompagnement.", 'danger');
      } else if (error.status === 471) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas de référent.", 'danger');
      } else if (error.status === 472) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas de structure.", 'danger');
      } else if (error.status === 473) {
        this.toastService.show(
          `Création impossible, un contrat ${this.contractTypeLabel(toSaveContract.contractType)} non terminé existe déjà sur la période.`,
          'danger',
        );
      } else if (error.status === 474) {
        this.toastService.show('Création impossible, un contrat existe déjà sur la période.', 'danger');
      } else {
        this.toastService.show(`Une erreur est survenue lors de la création`, 'danger');
      }
    }
  }

  private async updateContract(toSaveContract: ContractDto) {
    try {
      await this.contractsService.saveContract(toSaveContract);
      this.toastService.show('Le contrat a été modifié avec succès.', 'success');
      this.closeModal.emit();
    } catch (error) {
      if (error.status === 473) {
        this.toastService.show(
          `Modification impossible, un contrat ${this.contractTypeLabel(toSaveContract.contractType)} non terminé existe déjà sur la période.`,
          'danger',
        );
      } else if (error.status === 474) {
        this.toastService.show('Modification impossible, un contrat existe déjà sur la période.', 'danger');
      } else if (error.status === 475) {
        this.toastService.show(`Le contrat n'est pas modifiable`, 'danger');
      } else {
        this.toastService.show(`Une erreur est survenue lors de la sauvegarde`, 'danger');
      }
    }
  }

  public onStatusChanged(status: ContractStatus, init: boolean = false): void {
    if (status) {
      !init && this.contractDetailsForm.controls.statusDate.setValue(this.currentDate);
      if (!labelsByWorkflowedTypedContractType.includes(this.contractDetailsForm.controls.contractType.value)) {
        if (status === ContractStatus.Done && !this.user?.groups.includes('ADMIN')) {
          this.contractDetailsForm.controls.beginDate.disable({ emitEvent: false });
          this.contractDetailsForm.controls.endDate.disable({ emitEvent: false });
          this.contractDetailsForm.controls.contractType.disable({ emitEvent: false });
          this.contractDetailsForm.controls.mainPurpose.disable({ emitEvent: false });
          this.contractDetailsForm.controls.project.disable({ emitEvent: false });
          this.contractDetailsForm.controls.actionsToPerform.disable({ emitEvent: false });
          this.contractDetailsForm.controls.achievement.disable({ emitEvent: false });
        } else {
          this.contractDetailsForm.controls.beginDate.enable({ emitEvent: false });
          this.contractDetailsForm.controls.endDate.enable({ emitEvent: false });
          this.contractDetailsForm.controls.contractType.enable({ emitEvent: false });
          this.contractDetailsForm.controls.mainPurpose.enable({ emitEvent: false });
          this.contractDetailsForm.controls.project.enable({ emitEvent: false });
          this.contractDetailsForm.controls.actionsToPerform.enable({ emitEvent: false });
          this.contractDetailsForm.controls.achievement.enable({ emitEvent: false });
        }
      }
      this.contractDetailsForm.updateValueAndValidity();
    }
  }

  public onTypeChanged(type: ContractType, ignoreSetDate: boolean = true): void {
    if (type) {
      if (!WorkflowedContractType.includes(type)) {
        this.contractDetailsForm.setValidators(DateValidators.datesOrderValidator('beginDate', 'endDate'));
        this.contractDetailsForm.updateValueAndValidity();
        this.contractDetailsForm.controls.status.setValidators(Validators.required);
        this.contractDetailsForm.controls.status.enable({ emitEvent: false });
        this.contractDetailsForm.controls.achievement.enable();
        this.contractDetailsForm.controls.status.updateValueAndValidity();
      } else {
        this.contractDetailsForm.clearValidators();
        this.contractDetailsForm.setValidators(DateValidators.datesOrderValidator('beginDate', 'endDate'));
        this.contractDetailsForm.updateValueAndValidity();
        this.contractDetailsForm.controls.status.clearValidators();
        this.contractDetailsForm.controls.status.disable({ emitEvent: false });
        this.contractDetailsForm.controls.achievement.disable();
        this.contractDetailsForm.controls.achievement.setValue(null);
        this.contractDetailsForm.controls.status.updateValueAndValidity();
      }
      if (!ignoreSetDate) {
        this.setDates();
      }
    }
  }

  public async delete(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      this.closeModal.emit();
      await this.contractsService.delete(this.contract.id);
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce contrat ?');
  }

  private async openSignatureModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(SignatureComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: SignatureComponent = modal.componentInstance as SignatureComponent;
    modalComponent.title = 'Signature bénéficiaire';
    return modalComponent.confirm();
  }

  private async openCommentModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(CommentComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: CommentComponent = modal.componentInstance as CommentComponent;
    return modalComponent.confirm();
  }

  public async workflowAction(status: ContractTraceabilityStatus): Promise<void> {
    switch (status) {
      case ContractTraceabilityStatus.SignedBeneficiary: {
        const signatureDataURL: string = await this.openSignatureModal();
        if (signatureDataURL) {
          await this.updateStatus(
            new TraceabilityDto(this.contract.id, ContractTraceabilityStatus.SignedBeneficiary, undefined, signatureDataURL),
            'Signatures apposées, contrat maintenant en validation Métropole.',
            this.contract.contractType,
          );
        }
        break;
      }
      case ContractTraceabilityStatus.Unvalidated: {
        const comment: string = await this.openCommentModal();
        if (comment !== null) {
          await this.updateStatus(
            new TraceabilityDto(this.contract.id, ContractTraceabilityStatus.Unvalidated, comment, undefined),
            'Action réalisée avec succès.',
            this.contract.contractType,
          );
        }
        break;
      }
      default: {
        await this.updateStatus(
          new TraceabilityDto(this.contract.id, status, undefined, undefined),
          status === ContractTraceabilityStatus.SignedMetro && this.contract.status === ContractTraceabilityStatus.ValidatedMetro
            ? 'Contrat généré avec succès.'
            : 'Action réalisée avec succès.',
          this.contract.contractType,
        );
        break;
      }
    }
  }

  private async updateStatus(traceabilityDto: TraceabilityDto, success: string, contractType: string): Promise<void> {
    try {
      await this.contractsService.updateStatus(traceabilityDto, contractType);
      this.closeModal.emit();
      this.toastService.show(success, 'success');
    } catch (error) {
      if (error.status === 438) {
        this.toastService.show('Erreur durant la génération du contrat. Des signatures sont manquantes.', 'danger');
      } else if (error.status === 440) {
        this.toastService.show('Veuillez enregistrer une signature dans votre profil.', 'danger');
      } else if (error.status === 461) {
        this.toastService.show('Erreur durant la génération du contrat. Le dossier du bénéficiaire est incomplet.', 'danger');
      } else if (error.status === 473) {
        this.toastService.show(
          `Modification impossible, un contrat ${this.contractTypeLabel(contractType)} non terminé existe déjà sur la période.`,
          'danger',
        );
      }
    }
  }

  public setDates() {
    this.contractTypeIsSelected = true;
    this.contractDetailsForm.controls.mainPurpose.enable();
    this.contractDetailsForm.controls.project.enable();
    this.contractDetailsForm.controls.actionsToPerform.enable();
    this.contractDetailsForm.controls.beginDate.enable();
    this.contractDetailsForm.controls.endDate.enable();
    if (labelsByWorkflowedTypedContractType.includes(this.contractDetailsForm.controls.contractType.value)) {
      this.contractDetailsForm.controls.status.disable();
      this.contractDetailsForm.controls.achievement.disable();
    } else {
      this.contractDetailsForm.controls.status.enable();
      this.contractDetailsForm.controls.achievement.enable();
    }
    if (this.contractDetailsForm.controls.contractType.value === ContractType.SocioPro) {
      this.contractDetailsForm.controls.beginDate.setValue(this.datesNewContract.beginDateContractSocioPro);
      this.contractDetailsForm.controls.endDate.setValue(this.datesNewContract.endDateContractSocioPro);
    } else if (this.contractDetailsForm.controls.contractType.value === ContractType.StartingIERContract) {
      this.contractDetailsForm.controls.beginDate.setValue(this.datesNewContract.beginDateContractIER);
      this.contractDetailsForm.controls.endDate.setValue(this.datesNewContract.endDateContractIER);
    } else {
      this.contractDetailsForm.controls.beginDate.setValue(this.datesNewContract.beginDateContractClassic);
      this.contractDetailsForm.controls.endDate.setValue(this.datesNewContract.endDateContractClassic);
    }
  }

  // *********************************************************************************************
  // * UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS *
  // * UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS *
  // *********************************************************************************************

  private contractTypeLabel(contractType: ContractType | string): string {
    if (contractType === ContractType.StartingIERContract) {
      return 'IER';
    }
    if (contractType === ContractType.SocioPro) {
      return 'socio-professionnel';
    }
    return '';
  }
}
